import type { NavSection } from './navigation';

/**
 * Pure helpers for the sidebar's per-group collapse state and its localStorage
 * persistence. Kept free of React/DOM so they are unit-testable; the Sidebar
 * wires them to `localStorage` and `useState`.
 *
 * State shape: a map of `section.heading -> collapsed?` where `true` means the
 * group is collapsed. A group missing from the map falls back to its
 * `defaultOpen` (open unless `defaultOpen === false`).
 */

/** localStorage key under which the collapsed-groups map is persisted. */
export const NAV_COLLAPSE_STORAGE_KEY = 'ev.nav.collapsed';

export type CollapsedMap = Record<string, boolean>;

/** The default collapsed map derived from each section's `defaultOpen`. */
export function defaultCollapsed(sections: NavSection[]): CollapsedMap {
  const map: CollapsedMap = {};
  for (const s of sections) {
    if (s.defaultOpen === false) map[s.heading] = true;
  }
  return map;
}

/** Safely parses a persisted collapsed map; ignores malformed/unknown data. */
export function parsePersistedCollapsed(raw: string | null): CollapsedMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out: CollapsedMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'boolean') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Resolves the effective collapsed map by layering a (possibly stale) persisted
 * map over the per-section defaults. Persisted entries for headings that no
 * longer exist are dropped, so removing/renaming a group can't leave orphaned
 * state behind.
 */
export function resolveCollapsed(
  sections: NavSection[],
  persisted: CollapsedMap,
): CollapsedMap {
  const known = new Set(sections.map((s) => s.heading));
  const merged: CollapsedMap = defaultCollapsed(sections);
  for (const [heading, collapsed] of Object.entries(persisted)) {
    if (known.has(heading)) merged[heading] = collapsed;
  }
  return merged;
}

/** Serializes a collapsed map for persistence. */
export function serializeCollapsed(map: CollapsedMap): string {
  return JSON.stringify(map);
}
